import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { ExportFile } from '~/shared/data-contract/common';
import { exportFileSchema } from '~/shared/data-contract/common';
import type { ApiError } from '~/shared/data-contract/error';
import type {
    ProfilePermissions,
    UserCreatePayload,
    UserMe,
    UserMeCreate,
    UserMutation,
} from '~/shared/data-contract/user';
import { profilePermissionsSchema, userMeCreateSchema, userMeSchema } from '~/shared/data-contract/user';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { SkillsBanners } from '../../data-contract/banners';
import { skillsBannersSchema } from '../../data-contract/banners';
import type { CvAnalysisResponse } from '../../data-contract/cv-analysis';
import { useUserSettingsUiMutation } from './use-user-settings-ui';

const myProfileQueryStaleTime = 300_000; // 5 mins

export const useMyProfileQuery = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
    options?: { readonly isLoggedIn: boolean },
): UseQueryResult<UserMe> => {
    const pathname: Pathname = '/users/me';

    return useQuery({
        enabled: Boolean(options?.isLoggedIn),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userMeSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
        staleTime: 300_000,
    });
};

export const useMyProfilePermissionsQuery = (): UseQueryResult<ProfilePermissions> => {
    const pathname: Pathname = '/users/me/options';
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(profilePermissionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
        staleTime: myProfileQueryStaleTime,
    });
};

export const useCreateProfileMutation = (): UseMutationResult<UserMeCreate, Error, UserCreatePayload> => {
    const queryClient = useQueryClient();
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = '/users/me';

    const { userSettingsUiMutation } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);

    return useMutation<UserMeCreate, Error, UserCreatePayload>({
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.postJson(userMeCreateSchema, {
                pathname,
                payload: data,
                requestOptions: {
                    timeout: 30_000,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => {
            queryClient.clear();
            userSettingsUiMutation.mutate({ isFirstLoginPopupShown: true });
        },
    });
};

export const useProfileCvReimportReplaceMutation = (): UseMutationResult<
    UserMeCreate,
    Error,
    CvAnalysisResponse['profile'] | UserMutation
> => {
    const queryClient = useQueryClient();
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = '/users/me';

    return useMutation({
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.putJson(userMeCreateSchema, {
                pathname,
                payload: data,
                requestOptions: {
                    timeout: 30_000,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => {
            queryClient.clear();
        },
    });
};

export const useProfileCvReimportUpdateMutation = (): UseMutationResult<
    UserMeCreate,
    Error,
    CvAnalysisResponse['profile'] | UserMutation
> => {
    const queryClient = useQueryClient();
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = '/users/me';

    return useMutation({
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.patchJson(userMeCreateSchema, {
                pathname,
                payload: data,
                requestOptions: {
                    timeout: 30_000,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => {
            queryClient.clear();
        },
    });
};

export const useProfileExportMutation = (): UseMutationResult<ExportFile> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = '/users/me/export';

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.post(exportFileSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
    });
};

export const useSkillsBannersQuery = (): UseQueryResult<SkillsBanners, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = '/users/me/banners';

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillsBannersSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

const pathname: Pathname = '/users/me';

type ProfileCvReimportUpdateQueryProps = {
    readonly enabled: boolean;
    readonly overrideProfile: boolean;
    readonly profile: CvAnalysisResponse['profile'];
};

export const useProfileCvReimportUpdateQuery = ({
    enabled,
    overrideProfile,
    profile,
}: ProfileCvReimportUpdateQueryProps): UseQueryResult<CvAnalysisResponse['profile'], ApiError> => {
    const { applicationState, cobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;

    const operation = overrideProfile ? 'putJson' : 'patchJson';

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await cobrainerApiFetcher[operation]({
                pathname,
                payload: profile,
                requestOptions: {
                    searchParams: {
                        preview: 'true',
                    },
                },
            });

            if (result.isOk) {
                return result.value as CvAnalysisResponse['profile'];
            }

            throw result.error;
        },
        queryKey: [pathname, operation, profile, language],
    });
};
